<template>
  <q-page class="register-page">
    <div class="register-container">
      <!-- Left: Branding panel (desktop only) -->
      <div class="register-branding desktop-only">
        <div class="branding-content">
          <q-avatar rounded size="56px" class="q-mb-lg">
            <img src="../../assets/capitalflow-logo-light.jpeg" alt="CapitalFlow" />
          </q-avatar>
          <h2 class="branding-title">Trade smarter<br />with order flow.</h2>
          <p class="branding-subtitle">
            Real-time institutional order flow, powerful analytics, and actionable trade alerts — all in one platform.
          </p>
          <div class="branding-features">
            <div class="feature-item">
              <q-icon name="insights" size="20px" color="secondary" />
              <span>Live order flow data</span>
            </div>
            <div class="feature-item">
              <q-icon name="notifications_active" size="20px" color="secondary" />
              <span>Real-time trade alerts</span>
            </div>
            <div class="feature-item">
              <q-icon name="analytics" size="20px" color="secondary" />
              <span>Market analytics dashboard</span>
            </div>
            <div class="feature-item">
              <q-icon name="auto_graph" size="20px" color="secondary" />
              <span>Top contracts & movers</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Right: Form panel -->
      <div class="register-form-panel">
        <div class="form-wrapper">
          <!-- Mobile logo -->
          <div class="mobile-only text-center q-mb-lg">
            <q-avatar rounded size="48px" class="q-mb-sm">
              <img src="../../assets/capitalflow-logo-light.jpeg" alt="CapitalFlow" />
            </q-avatar>
          </div>

          <h1 class="form-title">Create your account</h1>
          <p class="form-subtitle">Get started for $20/wk — no credit card required to register.</p>

          <!-- Error banner -->
          <transition name="fade">
            <div v-if="error" class="error-banner">
              <q-icon name="error_outline" size="18px" />
              <span>{{ error }}</span>
            </div>
          </transition>

          <q-form ref="formRef" @submit="onSubmit" class="register-form">
            <!-- ═══ COLLAPSED SUMMARY CARD ═══ -->
            <transition name="fade">
              <div v-if="fieldsCollapsed" class="fields-summary-card">
                <div class="fields-summary-header">
                  <q-icon name="check_circle" size="18px" color="positive" />
                  <span class="fields-summary-title">Account Details</span>
                  <q-btn flat round dense icon="edit" size="sm" color="secondary" @click="editingFields = true" class="fields-edit-btn" />
                </div>
                <div class="fields-summary-body">
                  <div class="fields-summary-row">
                    <q-icon name="person" size="14px" color="grey-6" />
                    <span>{{ firstName }} {{ lastName }}</span>
                  </div>
                  <div class="fields-summary-row">
                    <q-icon name="email" size="14px" color="grey-6" />
                    <span>{{ email }}</span>
                  </div>
                  <div class="fields-summary-row">
                    <q-icon name="phone" size="14px" color="grey-6" />
                    <span>{{ phone }}</span>
                  </div>
                  <div v-if="experience" class="fields-summary-row">
                    <q-icon name="trending_up" size="14px" color="grey-6" />
                    <span>{{ experienceOptions.find(o => o.value === experience)?.label }} experience</span>
                  </div>
                </div>
              </div>
            </transition>

            <!-- ═══ EXPANDABLE FORM FIELDS ═══ -->
            <transition name="slide">
              <div v-show="!fieldsCollapsed" class="fields-expandable">
                <!-- Step 1: Name -->
                <div class="form-row">
                  <q-input
                    ref="firstNameRef"
                    dark outlined dense
                    hide-bottom-space
                    color="secondary"
                    v-model="firstName"
                    label="First name"
                    lazy-rules
                    :rules="[val => !!val || 'Required']"
                    class="form-field"
                  />
                  <q-input
                    ref="lastNameRef"
                    dark outlined dense
                    hide-bottom-space
                    color="secondary"
                    v-model="lastName"
                    label="Last name"
                    lazy-rules
                    :rules="[val => !!val || 'Required']"
                    class="form-field"
                  />
                </div>

                <!-- Email -->
                <q-input
                  ref="emailRef"
                  dark outlined dense
                  hide-bottom-space
                  color="secondary"
                  v-model="email"
                  type="email"
                  label="Email address"
                  lazy-rules
                  :rules="fieldEmailRules"
                />

                <!-- Password -->
                <div class="form-row">
                  <q-input
                    ref="passwordRef"
                    dark outlined dense
                    hide-bottom-space
                    color="secondary"
                    :type="showPassword ? 'text' : 'password'"
                    v-model="password"
                    label="Password"
                    lazy-rules
                    :rules="fieldPasswordRules"
                    autocomplete="new-password"
                    class="form-field"
                  >
                    <template v-slot:append>
                      <q-icon
                        :name="showPassword ? 'visibility_off' : 'visibility'"
                        class="cursor-pointer"
                        size="18px"
                        @click="showPassword = !showPassword"
                      />
                    </template>
                  </q-input>
                  <q-input
                    ref="confirmPasswordRef"
                    dark outlined dense
                    hide-bottom-space
                    color="secondary"
                    :type="showPassword ? 'text' : 'password'"
                    v-model="confirmPassword"
                    label="Confirm password"
                    lazy-rules
                    :rules="fieldConfirmPasswordRules"
                    autocomplete="new-password"
                    class="form-field"
                  />
                </div>

                <!-- Phone -->
                <q-input
                  ref="phoneRef"
                  dark outlined dense
                  hide-bottom-space
                  color="secondary"
                  v-model="phone"
                  type="tel"
                  label="Phone number"
                  hint="For account verification only — we won't spam you"
                  lazy-rules
                  :rules="fieldPhoneRules"
                  @keyup="updatePhone"
                  @input="handleChangePhone($event.target.value)"
                  @paste="handlePastePhone"
                />

                <!-- Experience chips -->
                <div class="experience-section">
                  <div class="experience-label">Trading experience</div>
                  <div class="experience-chips">
                    <q-btn
                      v-for="opt in experienceOptions"
                      :key="opt.value"
                      :label="opt.label"
                      no-caps
                      unelevated
                      :color="experience === opt.value ? 'secondary' : undefined"
                      :text-color="experience === opt.value ? 'white' : 'grey-4'"
                      :class="['experience-chip', { 'experience-chip--active': experience === opt.value }]"
                      @click="experience = opt.value"
                    />
                  </div>
                </div>
              </div>
            </transition>

            <!-- Compliance section — collapsible -->
            <div class="compliance-section">
              <q-checkbox
                dark dense
                v-model="nonProfessional"
                color="secondary"
                class="compliance-check"
              >
                <span class="compliance-text">
                  I am a <strong class="text-accent">Non-Professional</strong> subscriber
                </span>
              </q-checkbox>

              <transition name="slide">
                <div v-if="nonProfessional" class="attestation-list">
                  <label
                    v-for="(item, i) in attestations"
                    :key="i"
                    class="attestation-item"
                  >
                    <q-checkbox
                      dark dense
                      v-model="item.checked"
                      color="secondary"
                      size="sm"
                    />
                    <span class="attestation-text" v-html="item.text" />
                  </label>
                </div>
              </transition>

              <q-checkbox
                dark dense
                v-model="tc"
                color="secondary"
                class="compliance-check q-mt-sm"
              >
                <span class="compliance-text">
                  I agree to the
                  <a class="text-accent" @click.prevent="onTCOpen">Terms</a>,
                  <a class="text-accent" @click.prevent="onPrivacyOpen">Privacy Policy</a>, and
                  <a class="text-accent" @click.prevent="onRefundPolicyOpen">Refund Policy</a>
                </span>
              </q-checkbox>
            </div>

            <!-- IFS Agreement (shown after T&C accepted) -->
            <transition name="fade">
              <div v-if="tc" class="ifs-notice">
                Your receipt and use of this service is subject to the
                <a class="text-accent" target="_blank" href="https://assets.capitalflow.app/legal/IFS_OPRAB1electronic_subscriber.pdf">
                  IFS subscriber agreement</a>.
              </div>
            </transition>

            <!-- Submit -->
            <q-btn
              type="submit"
              color="secondary"
              no-caps
              unelevated
              class="submit-btn"
              :loading="loading"
              :disable="loading"
              label="Create Account"
              size="lg"
            />
          </q-form>

          <!-- Sign in link -->
          <div class="signin-link">
            Already have an account?
            <button class="link-btn" @click="onSignIn">Sign in</button>
          </div>
        </div>
      </div>
    </div>
  </q-page>
</template>

<script>
import { ref, reactive, onMounted, onUpdated, watch } from "vue"
import { useRouter, useRoute } from "vue-router"
import { loggerFactory } from '../../logger'
import { useUserStore } from '../../stores/user-store'
import { useQuasar } from "quasar"
import { captureError, getReferralCode, referralTrackLead, saveReferralCode, track } from '../../rum'
import { AsYouType, isPossiblePhoneNumber, parsePhoneNumber } from "libphonenumber-js"

const logger = loggerFactory("RegisterPage")
export default {
  name: "RegisterPage",
  setup() {
    const $q = useQuasar()
    const router = useRouter()
    const route = useRoute()
    const store = useUserStore()

    const error = ref(null)
    const loading = ref(false)
    const showPassword = ref(false)
    const editingFields = ref(false)

    // Form fields
    const formRef = ref(null)
    const firstName = ref(null)
    const firstNameRef = ref(null)
    const lastName = ref(null)
    const lastNameRef = ref(null)
    const phone = ref(null)
    const phoneRef = ref(null)
    const email = ref(null)
    const emailRef = ref(null)
    const confirmPassword = ref(null)
    const confirmPasswordRef = ref(null)
    const password = ref(null)
    const passwordRef = ref(null)

    // Experience
    const experience = ref(null)
    const experienceOptions = [
      { label: "< 1 year", value: "beginner" },
      { label: "1–3 years", value: "intermediate" },
      { label: "5+ years", value: "expert" }
    ]

    // Compliance
    const nonProfessional = ref(false)
    const tc = ref(false)
    const tosAcceptedAt = ref(null)
    const attestations = reactive([
      {
        checked: false,
        text: 'I am <strong class="text-accent">not</strong> registered or qualified with the SEC, CFTC, any state securities agency, or any securities exchange/association.'
      },
      {
        checked: false,
        text: 'I am <strong class="text-accent">not</strong> engaged as an "investment advisor" as defined in the <strong class="text-accent">Investment Advisers Act of 1940</strong>.'
      },
      {
        checked: false,
        text: 'I am <strong class="text-accent">not</strong> employed by a bank or exempt organization to perform functions requiring registration.'
      }
    ])

    // Validation rules
    const fieldPhoneRules = ref([
      val => !!val || 'Phone number is required',
      val => isPossiblePhoneNumber(val) || 'Enter a valid phone number'
    ])
    const fieldEmailRules = ref([
      val => !!val || "Email is required",
      val => /.+@.+\..+/.test(val) || "Enter a valid email address"
    ])
    const fieldPasswordRules = ref([
      val => !!val || "Password is required",
      val => (val && val.length >= 6) || "Password must be at least 6 characters"
    ])
    const fieldConfirmPasswordRules = ref([
      val => val === password.value || "Passwords don't match"
    ])

    // Phone formatting
    const handleNumberInput = number => {
      if (number) {
        const prefix = "+"
        let formatted = ""
        if (number.includes(prefix)) {
          formatted = new AsYouType().input(number)
        } else {
          formatted = new AsYouType().input("+1" + number)
        }
        phone.value = formatted
      }
    }
    const updatePhone = () => handleNumberInput(phone.value)
    const handleChangePhone = phoneNumber => handleNumberInput(phoneNumber)
    const handlePastePhone = () => setTimeout(updatePhone, 100)

    // Navigation
    const onSignIn = () => router.replace({ path: '/auth/login' })
    const onTCOpen = () => window.open("https://app.termly.io/document/terms-of-use-for-website/2b52ab92-90ec-42ed-9196-0b9bd5cab6d1", '_blank')
    const onPrivacyOpen = () => window.open("https://app.termly.io/document/privacy-policy/3f68fc03-47d8-45bc-8f1f-a91fac7241c8", '_blank')
    const onRefundPolicyOpen = () => window.open("https://app.termly.io/document/return-policy/f9ac2e86-6892-4fb0-ba55-50d4b756af9a", '_blank')

    const notifyError = (message) => {
      $q.notify({ type: "negative", message, timeout: 3000, position: "top" })
    }

    // Submit
    const onSubmit = () => {
      track("signup_action", { email: email.value, experience: experience.value })

      formRef.value.validate().then(success => {
        // Validate non-form fields
        if (!experience.value) {
          track("signup_validation_failed", { email: email.value, reason: "missing_experience" })
          notifyError("Please select your trading experience.")
          return
        }
        if (!nonProfessional.value || !attestations.every(a => a.checked)) {
          track("signup_validation_failed", { email: email.value, reason: "missing_non_professional_attestation" })
          notifyError("Please complete the non-professional attestation.")
          return
        }
        if (!tc.value) {
          track("signup_validation_failed", { email: email.value, reason: "missing_tos" })
          notifyError("Please accept the Terms & Conditions.")
          return
        }

        let parsedNumber = {}
        try {
          parsedNumber = parsePhoneNumber(phone.value)
        } catch (e) {
          parsedNumber.number = ""
        }
        const number = parsedNumber.number

        if (success && number) {
          loading.value = true
          const finalEmail = email.value.trim()
          const finalPassword = password.value.trim()

          store.signup(finalEmail, finalPassword, number, firstName.value, lastName.value, experience.value, tosAcceptedAt.value)
            .then((user) => {
              logger.debug('Signed up new user', user)
              referralTrackLead(user?.id)
              const referralCode = getReferralCode() || route.params?.referral || null
              if (referralCode) saveReferralCode(referralCode)
              track("signup", {
                email: finalEmail,
                phone: number,
                referral: referralCode,
                experience: experience.value
              })
              router.replace({ path: '/start' })
            })
            .catch((err) => {
              logger.error("Signup failed on RegisterPage", err)
              error.value = err.message
              track("signup_error", { email: finalEmail, reason: err.message })
            })
            .finally(() => loading.value = false)
        } else {
          track("signup_validation_failed", { email: email.value, reason: !number ? "invalid_phone" : "form_validation_failed" })
          notifyError("Please complete all fields before continuing.")
        }
      })
    }

    onMounted(() => {
      logger.debug("Route: ", route.path, route.params?.referral)
      if (route.params?.referral) saveReferralCode(route.params.referral)
      if (route.query?.referral) saveReferralCode(route.query.referral)
      store.currentUser()
        .then((user) => {
          if (user) logger.debug("Loaded user, skip login...", user)
        })
        .catch((err) => logger.info('Unable to load session', err))
    })

    onUpdated(() => {
      if (phoneRef.value) {
        handleChangePhone(phoneRef.value.value)
      }
    })

    // Computed: collapse fields when non-professional is checked and not editing
    const fieldsCollapsed = ref(false)
    watch(nonProfessional, (val) => {
      if (val && !editingFields.value) {
        fieldsCollapsed.value = true
      }
    })
    watch(editingFields, (val) => {
      fieldsCollapsed.value = !val && nonProfessional.value
    })

    // Capture ToS acceptance timestamp
    watch(tc, (accepted) => {
      if (accepted && !tosAcceptedAt.value) {
        tosAcceptedAt.value = new Date().toISOString()
      } else if (!accepted) {
        tosAcceptedAt.value = null
      }
    })

    return {
      error, loading, showPassword, editingFields, fieldsCollapsed,
      formRef,
      firstName, firstNameRef,
      lastName, lastNameRef,
      phone, phoneRef,
      email, emailRef,
      password, passwordRef,
      confirmPassword, confirmPasswordRef,
      experience, experienceOptions,
      nonProfessional, tc, attestations,
      fieldPhoneRules, fieldEmailRules,
      fieldPasswordRules, fieldConfirmPasswordRules,
      onSubmit, onSignIn,
      onTCOpen, onPrivacyOpen, onRefundPolicyOpen,
      updatePhone, handleChangePhone, handlePastePhone,
    }
  }
}
</script>

<style lang="scss" scoped>
// ─── Layout ───
.register-page {
  min-height: 100vh;
  background: $dark-page;
}

.register-container {
  display: flex;
  min-height: 100vh;
}

// ─── Branding Panel ───
.register-branding {
  flex: 0 0 420px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px;
  background: linear-gradient(
    135deg,
    rgba($secondary, 0.08) 0%,
    rgba($primary, 0.95) 50%,
    rgba($dark-page, 1) 100%
  );
  border-right: 1px solid rgba($white, 0.04);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -120px;
    right: -120px;
    width: 360px;
    height: 360px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba($secondary, 0.12) 0%, transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -80px;
    left: -80px;
    width: 240px;
    height: 240px;
    border-radius: 50%;
    background: radial-gradient(circle, rgba($accent, 0.06) 0%, transparent 70%);
    pointer-events: none;
  }
}

.branding-content {
  position: relative;
  z-index: 1;
}

.branding-title {
  font-size: 32px;
  font-weight: 700;
  line-height: 1.2;
  color: $white;
  margin: 0 0 16px 0;
  letter-spacing: -0.5px;
}

.branding-subtitle {
  font-size: 15px;
  line-height: 1.6;
  color: $text-secondary;
  margin: 0 0 32px 0;
}

.branding-features {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  color: rgba($white, 0.8);
}

// ─── Form Panel ───
.register-form-panel {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  overflow-y: auto;
}

.form-wrapper {
  width: 100%;
  max-width: 480px;
}

.form-title {
  font-size: 26px;
  font-weight: 700;
  color: $white;
  margin: 0 0 8px 0;
  letter-spacing: -0.3px;
}

.form-subtitle {
  font-size: 14px;
  color: $text-secondary;
  margin: 0 0 28px 0;
}

// ─── Error Banner ───
.error-banner {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: rgba($negative, 0.1);
  border: 1px solid rgba($negative, 0.25);
  border-radius: $border-radius-sm;
  color: $negative;
  font-size: 13px;
  margin-bottom: 20px;
}

// ─── Form ───
.register-form {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.form-row {
  display: flex;
  gap: 12px;

  .form-field {
    flex: 1;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 14px;
  }
}

// Input overrides
.register-form :deep(.q-field--outlined .q-field__control) {
  border-radius: $border-radius-sm;
  background: rgba($white, 0.03);
  border-color: rgba($white, 0.1);
  transition: border-color $transition-fast, box-shadow $transition-fast;

  &:hover {
    border-color: rgba($white, 0.18);
  }
}

.register-form :deep(.q-field--outlined.q-field--focused .q-field__control) {
  border-color: rgba($secondary, 0.6);
  box-shadow: 0 0 0 2px rgba($secondary, 0.12);
}

.register-form :deep(.q-field--outlined.q-field--error .q-field__control) {
  border-color: rgba($negative, 0.5);
}

// ─── Experience Chips ───
.experience-section {
  margin: 4px 0;
}

.experience-label {
  font-size: 13px;
  color: $text-secondary;
  font-weight: 500;
  margin-bottom: 8px;
}

.experience-chips {
  display: flex;
  gap: 8px;
}

.experience-chip {
  border: 1px solid rgba($white, 0.1) !important;
  background: rgba($white, 0.03) !important;
  border-radius: $border-radius-sm !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  padding: 6px 16px !important;
  transition: all $transition-fast !important;

  &:hover {
    border-color: rgba($secondary, 0.4) !important;
    background: rgba($secondary, 0.06) !important;
  }

  &--active {
    border-color: rgba($secondary, 0.5) !important;
    background: $secondary !important;
    box-shadow: 0 0 0 1px rgba($secondary, 0.3);
  }
}

// ─── Fields Summary Card ───
.fields-summary-card {
  padding: 14px 16px;
  background: rgba($secondary, 0.06);
  border: 1px solid rgba($secondary, 0.18);
  border-radius: $border-radius-md;
  transition: all $transition-normal;
}

.fields-summary-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.fields-summary-title {
  font-size: 13px;
  font-weight: 600;
  color: rgba($white, 0.9);
  flex: 1;
}

.fields-edit-btn {
  opacity: 0.7;
  transition: opacity $transition-fast;

  &:hover {
    opacity: 1;
  }
}

.fields-summary-body {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.fields-summary-row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: rgba($white, 0.7);
  line-height: 1.4;
}

.fields-expandable {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

// ─── Compliance ───
.compliance-section {
  padding: 16px;
  background: rgba($white, 0.02);
  border: 1px solid rgba($white, 0.05);
  border-radius: $border-radius-md;
}

.compliance-check {
  margin: 0;
}

.compliance-text {
  font-size: 13px;
  color: rgba($white, 0.8);
  line-height: 1.4;

  a {
    cursor: pointer;
    text-decoration: none;
    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }
}

.attestation-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 0 4px 8px;
  margin: 8px 0;
  border-left: 2px solid rgba($secondary, 0.3);
}

.attestation-item {
  display: flex;
  align-items: flex-start;
  gap: 4px;
  cursor: pointer;
}

.attestation-text {
  font-size: 12px;
  color: rgba($white, 0.65);
  line-height: 1.5;
  padding-top: 2px;
}

// ─── IFS Notice ───
.ifs-notice {
  font-size: 11px;
  color: $text-muted;
  line-height: 1.5;
  padding: 8px 12px;
  background: rgba($white, 0.02);
  border-radius: $border-radius-sm;

  a {
    font-weight: 600;
  }
}

// ─── Submit Button ───
.submit-btn {
  width: 100%;
  margin-top: 8px;
  border-radius: $border-radius-sm !important;
  font-weight: 600;
  letter-spacing: 0.2px;
  transition: all $transition-fast;

  &:hover {
    box-shadow: 0 4px 20px rgba($secondary, 0.3);
    transform: translateY(-1px);
  }
}

// ─── Sign-in Link ───
.signin-link {
  text-align: center;
  margin-top: 24px;
  font-size: 14px;
  color: $text-secondary;
}

.link-btn {
  background: none;
  border: none;
  color: $secondary;
  font-weight: 600;
  font-size: 14px;
  cursor: pointer;
  padding: 0;
  text-decoration: none;
  font-family: inherit;

  &:hover {
    text-decoration: underline;
  }
}

// ─── Transitions ───
.fade-enter-active,
.fade-leave-active {
  transition: opacity $transition-normal;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: all $transition-normal;
  overflow: hidden;
}
.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}
.slide-enter-to,
.slide-leave-from {
  max-height: 400px;
  opacity: 1;
}

// ─── Responsive ───
@media (max-width: 768px) {
  .register-container {
    flex-direction: column;
  }

  .register-form-panel {
    padding: 24px 16px;
  }

  .form-title {
    font-size: 22px;
    text-align: center;
  }

  .form-subtitle {
    text-align: center;
  }
}
</style>
